/**
 * antesDeSair — o gancho que o `logout()` chama ANTES de apagar o token (R4, sobras das ligações).
 *
 * Sair do sistema no meio de uma ligação deixava o outro lado sozinho na sala: o `logout()` do `AuthContext`
 * é síncrono e limpa o storage na hora, então quando o `CallProvider` desmontava e mandava o `/end` (ou o
 * `/leave`) o pedido já saía sem `Authorization` e levava 401. Agora quem tem algo a encerrar no servidor se
 * registra aqui, e o `logout()` o chama com o access token de QUEM SAI, capturado antes da limpeza; o
 * registrante manda o pedido com esse token (um `fetch` com `keepalive`, que a navegação não cancela).
 *
 * O módulo é um barramento de um assunto só, no molde do `eventosDaChamada`: o `AuthProvider` e o
 * `CallProvider` não se conhecem.
 */

/** Como a saída está acontecendo. Sem opções, é o logout comum: o access ainda vale e o gancho pode falar com o servidor. */
export interface OpcoesDaSaida {
  /**
   * O servidor já encerrou a sessão ("Sair de todos os aparelhos") e o access está revogado: qualquer pedido com ele leva 401. O gancho
   * não manda pedido nenhum e faz só o que é local.
   */
  semRede?: boolean;
}

/** Recebe o access token de quem está saindo (o storage ainda o guarda, mas é o que o `logout` capturou) e, se vier, como a saída é. */
export type AntesDeSair = (tokenDeQuemSai: string, opcoes?: OpcoesDaSaida) => void;

const ganchos = new Set<{ chamar: AntesDeSair }>();

/** Registra um gancho. Devolve a função que o cancela (idempotente). */
export function registrarAntesDeSair(fn: AntesDeSair): () => void {
  // Um objeto por registro: o mesmo `fn` registrado duas vezes roda duas vezes, e cada cancelamento tira só o seu.
  const registro = { chamar: fn };
  ganchos.add(registro);
  return () => {
    ganchos.delete(registro);
  };
}

/** Chama todos os ganchos. Um que lança vai ao `console.error` e não impede os outros nem o logout. */
export function executarAntesDeSair(tokenDeQuemSai: string, opcoes?: OpcoesDaSaida): void {
  // Cópia: um gancho pode se cancelar (ou registrar outro) durante a execução.
  for (const gancho of [...ganchos]) {
    if (!ganchos.has(gancho)) continue;
    try {
      // Sem opções o gancho recebe só o token, como sempre foi (o logout comum não muda).
      if (opcoes) gancho.chamar(tokenDeQuemSai, opcoes);
      else gancho.chamar(tokenDeQuemSai);
    } catch (erro) {
      console.error("[chamadas] gancho antes de sair falhou:", erro);
    }
  }
}
